import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, MoreHorizontal, Search } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { TablePagination } from './TablePagination';
export interface RowAction<T> {
  label: string;
  icon: React.ElementType;
  onClick: (row: T) => void;
  danger?: boolean;
}
export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (value: unknown, row: T) => ReactNode;
  sortable?: boolean;
  width?: string;
}
interface DataTableProps<T extends Record<string, unknown>> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  className?: string;
  rowActions?: RowAction<T>[];
  /** Enable built-in search filtering */
  searchable?: boolean;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Keys to search across (defaults to all string/number column keys) */
  searchKeys?: (keyof T)[];
  /** Enable pagination */
  pagination?: boolean;
  /** Default page size if pagination is enabled */
  pageSize?: number;
  /** Enable row selection */
  selectable?: boolean;
  /** Currently selected row IDs */
  selectedIds?: string[];
  /** Callback when selection changes */
  onSelectionChange?: (selectedIds: string[]) => void;
}
export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  emptyMessage = 'Geen gegevens beschikbaar',
  className = '',
  rowActions,
  searchable = false,
  searchPlaceholder = 'Zoeken...',
  searchKeys,
  pagination = false,
  pageSize = 50,
  selectable = false,
  selectedIds = [],
  onSelectionChange
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  // Filter data by search query
  const filteredData = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return data;
    const query = searchQuery.toLowerCase();
    const keys = searchKeys || columns.map(c => String(c.key));
    return data.filter(row => keys.some(key => {
      const val = row[String(key)];
      if (val == null) return false;
      return String(val).toLowerCase().includes(query);
    }));
  }, [data, searchable, searchQuery, searchKeys, columns]);
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortKey) return 0;
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (aVal === bVal) return 0;
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    const cmp = typeof aVal === 'number' && typeof bVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal), 'nl');
    return sortDir === 'asc' ? cmp : -cmp;
  });
  // Reset page when search changes
  useMemo(() => {
    setPage(0);
  }, [searchQuery]);
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    const start = page * currentPageSize;
    return sortedData.slice(start, start + currentPageSize);
  }, [sortedData, pagination, page, currentPageSize]);
  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (selectedIds.length === sortedData.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(sortedData.map((row, i) => String(row.id ?? i)));
    }
  };
  const handleSelectRow = (rowId: string) => {
    if (!onSelectionChange) return;
    if (selectedIds.includes(rowId)) {
      onSelectionChange(selectedIds.filter(id => id !== rowId));
    } else {
      onSelectionChange([...selectedIds, rowId]);
    }
  };
  return <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col ${className}`} data-id="element-6727">
      {searchable && <div className="px-4 py-3 border-b border-gray-100" data-id="element-6728">
          <div className="w-full sm:max-w-sm" data-id="element-6729">
            <Input type="text" placeholder={searchPlaceholder} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-6731" />} size="sm" data-id="element-6730" />
          </div>
          {searchQuery && <div className="text-xs text-gray-400 mt-1.5" data-id="element-6732">
              {filteredData.length} van {data.length} resultaten
            </div>}
        </div>}

      <div className="overflow-x-auto overflow-y-auto relative shadow-[inset_-12px_0_8px_-12px_rgba(0,0,0,0.1)] sm:shadow-none flex-1 max-h-[calc(100vh-280px)]" data-id="element-6733">
        <table className="w-full text-sm min-w-[640px]" data-id="element-6734">
          <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-100 shadow-sm" data-id="element-6735">
            <tr data-id="element-6736">
              {selectable && <th className="px-4 py-3 w-12 text-left" data-id="element-6737">
                  <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" checked={sortedData.length > 0 && selectedIds.length === sortedData.length} ref={input => {
                if (input) {
                  input.indeterminate = selectedIds.length > 0 && selectedIds.length < sortedData.length;
                }
              }} onChange={handleSelectAll} data-id="element-6738" />
                </th>}
              {columns.map(col => <th key={String(col.key)} className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide ${col.sortable !== false ? 'cursor-pointer hover:text-gray-700 select-none' : ''}`} style={col.width ? {
              width: col.width
            } : undefined} onClick={() => col.sortable !== false && handleSort(String(col.key))} data-id="element-6739">
                  <div className="flex items-center gap-1" data-id="element-6740">
                    {col.label}
                    {col.sortable !== false && sortKey === String(col.key) && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" data-id="element-6741" /> : <ChevronDown className="w-3 h-3" data-id="element-6742" />)}
                  </div>
                </th>)}
              {rowActions && rowActions.length > 0 && <th className="px-4 py-3 w-10" data-id="element-6743"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50" data-id="element-6744">
            {paginatedData.length === 0 ? <tr data-id="element-6745">
                <td colSpan={columns.length + (rowActions ? 1 : 0) + (selectable ? 1 : 0)} className="px-4 py-12 text-center text-sm text-gray-400" data-id="element-6746">
                  {emptyMessage}
                </td>
              </tr> : paginatedData.map((row, i) => {
            const rowId = String(row.id ?? i);
            const isSelected = selectedIds.includes(rowId);
            return <tr key={rowId} onClick={() => onRowClick?.(row)} className={`transition-colors ${isSelected ? 'bg-blue-50/50' : ''} ${onRowClick ? 'cursor-pointer hover:bg-blue-50/50' : 'hover:bg-gray-50'}`} data-id="element-6747">
                    {selectable && <td className="px-4 py-3" onClick={e => e.stopPropagation()} data-id="element-6748">
                        <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" checked={isSelected} onChange={() => handleSelectRow(rowId)} data-id="element-6749" />
                      </td>}
                    {columns.map(col => <td key={String(col.key)} className="px-4 py-3" data-id="element-6750">
                        {col.render ? col.render(row[String(col.key)], row) : <span className="text-gray-700" data-id="element-6751">
                            {String(row[String(col.key)] ?? '—')}
                          </span>}
                      </td>)}
                    {rowActions && rowActions.length > 0 && <td className="px-4 py-3 text-right relative" onClick={e => e.stopPropagation()} data-id="element-6752">
                        <button onClick={() => setOpenMenuId(openMenuId === rowId ? null : rowId)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-6753">
                          <MoreHorizontal className="w-4 h-4" data-id="element-6754" />
                        </button>

                        {openMenuId === rowId && <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} data-id="element-6755" />
                            <div className="absolute right-0 sm:right-8 top-10 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20" data-id="element-6756">
                              {rowActions.map((action, idx) => {
                      const Icon = action.icon;
                      return <button key={idx} onClick={() => {
                        setOpenMenuId(null);
                        action.onClick(row);
                      }} className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left transition-colors ${action.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-6757">
                                    <Icon className="w-4 h-4" data-id="element-6758" />
                                    {action.label}
                                  </button>;
                    })}
                            </div>
                          </>}
                      </td>}
                  </tr>;
          })}
          </tbody>
        </table>
      </div>

      {pagination && <TablePagination page={page} pageSize={currentPageSize} total={sortedData.length} onPageChange={setPage} onPageSizeChange={size => {
      setCurrentPageSize(size);
      setPage(0);
    }} data-id="element-6759" />}
    </div>;
}